import { useCallback, useEffect, useState } from 'react';
import { trophiesService } from '../../services/trophies';
import type { Trophy } from '../../types/trophy';
import { useGymAdmin } from '../context/gym-admin-context';

/**
 * Loads the full Trophy record for the circuit currently selected in the
 * sidebar — the counterpart of `useCurrentGym`, for the pages that need the
 * whole thing (its stages, its categories) rather than just an id.
 */
export function useCurrentTrophy() {
  const { selectedGymSlug, locationType } = useGymAdmin();
  const [trophy, setTrophy] = useState<Trophy | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (slug: string) => {
    setLoading(true);
    setError(null);
    try {
      setTrophy(await trophiesService.getTrophyBySlug(slug));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load trophy');
      setTrophy(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Guarded on the kind as well as the slug: switching from a trophy to a gym
    // would otherwise fire one last fetch for a slug this endpoint 404s on.
    if (!selectedGymSlug || locationType !== 'trophy') {
      setTrophy(null);
      setLoading(false);
      return;
    }
    load(selectedGymSlug);
  }, [selectedGymSlug, locationType, load]);

  const reload = useCallback(() => {
    if (selectedGymSlug && locationType === 'trophy') load(selectedGymSlug);
  }, [selectedGymSlug, locationType, load]);

  return { trophy, loading, error, setTrophy, reload, slug: selectedGymSlug };
}
